import React, { useMemo, useState } from 'react';

import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { useTranslation } from 'next-i18next';

import Slider from 'src/components/ui/Slider';
import formatPrice from 'src/utils/formatPrice';
import getPriceEvaluation from 'src/utils/getPriceEvaluation';
import Icon from 'src/components/ui/Icon';
import { colours, gradients } from 'src/constants/colours';
import { styledComponent } from 'src/utils/styled';
import PriceEvaluationTag from 'src/components/ui/PriceEvaluationTag';
import useBreakpoints from 'src/hooks/useBreakpoints';
import type { Ad } from 'src/types/__generated__/graphql';
import Button from 'src/components/ui/Button';
import { useRequestPropertyEstimation } from '../hooks/useRequestPropertyEstimation';
import Loader from 'src/components/ui/Loader';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';

const priceEvalPercent: Record<PriceEvaluation, number> = {
    expensive: 25,
    good: 55,
    veryGood: 80,
};

const ContentItem = styledComponent(Box)`
    text-align: center;
`;

const EvaluationSlider = styledComponent(Slider)`
    width: 100%;
    padding: 0.5rem 0;
    margin-top: 1.875rem;
    .MuiSlider-rail {
        opacity: 1;
        background: ${gradients.linearEstimate};
        height: 1rem;
    }
    .MuiSlider-thumb {
        border-radius: 5rem;
        background-color: ${colours.primary.main};
        border: 0.125rem solid ${colours.primary.O04};
        position: relative;
        width: 0.5rem;
        height: 1.25rem;
        &::before {
            box-shadow: none;
        }
        & .MuiSlider-valueLabel, & .MuiSlider-valueLabelOpen {
            /* tooltip */
            background: ${colours.primary.main};
            color: ${colours.white.main};
            transform: translateY(-1.125rem) scale(1);
            height: 1.5rem;
            border-radius: 8px;
            font-weight: 700;
            font-size: 1rem;
            display: flex;
            justify-content: center;
            align-items: center;
            line-height: 1;
        }
        & .MuiSlider-valueLabel:before {
            /* tooltip pointer*/
            display: none;
        }
    }
`;

const RadiusBox = styledComponent(Box)`
    padding: 1.5rem;
    border-radius: 1rem;
    background: ${colours.black.O04};
`;

const BlurBox = styledComponent(Box)`
    position: absolute;
    height: 110%;
    width: 100%;
    background: ${colours.white.O50};
    backdrop-filter: blur(0.5rem);
`;

type Props = {
    ad: {
        id: Ad['id'];
        price: number;
        livingArea: number;
    };
    evaluation?: HomadataEvaluation;
};

export const PropertyEvaluationSection: React.FC<Props> = ({ ad, evaluation }) =>
    evaluation ? (
        <Evaluation ad={ad} evaluation={evaluation} evaluationStatus="visible" />
    ) : (
        <RequestEvaluation ad={ad} />
    );

type EvaluationProps = {
    ad: {
        price: number;
        livingArea: number;
    };
    evaluation: HomadataEvaluation;
    onRequestEvaluation?: () => void;
    evaluationStatus: 'visible' | 'hidden' | 'loading' | 'unavailable';
};
const Evaluation: React.FC<EvaluationProps> = ({
    ad,
    evaluation: { confidenceMin, confidenceMax, predictedPrice },
    onRequestEvaluation,
    evaluationStatus,
}) => {
    const { t, i18n } = useTranslation(['ad']);
    const { isUnder500 } = useBreakpoints();

    const priceEval = getPriceEvaluation(ad.price, confidenceMin, confidenceMax);
    const estimateBounds = [
        {
            alignItems: 'flex-start',
            price: confidenceMin,
            dataTargetId: 'home-evaluation-min-confidence',
            textKey: 'ad:homeEvaluation.confidenceMin',
        },
        {
            alignItems: 'flex-end',
            price: confidenceMax,
            dataTargetId: 'home-evaluation-max-confidence',
            textKey: 'ad:homeEvaluation.confidenceMax',
        },
    ];

    const labelContent = useMemo(
        // eslint-disable-next-line react/no-unstable-nested-components, react/function-component-definition -- already memoized and function is a valid format
        () => () => (
            <span data-target-id="home-evaluation-predicted-price">
                {formatPrice({ price: Number(predictedPrice), lang: i18n.language })}
            </span>
        ),
        [predictedPrice, i18n],
    );
    const tag = evaluationStatus === 'visible' && (
        <PriceEvaluationTag
            price={ad.price}
            confidenceMin={confidenceMin}
            confidenceMax={confidenceMax}
            size="tiny"
            dataTestIdPrefix="evaluationSection"
        />
    );
    const sellerPrice = (
        <Typography component="span" variant="h3" data-target-id="home-evaluation-title" sx={{ lineHeight: '1.5rem' }}>
            {formatPrice({ price: ad.price, lang: i18n.language })}
        </Typography>
    );
    const sqMeters = (
        <Typography component="span" color={colours.primary.main} mb={1} sx={{ lineHeight: '1.125rem' }}>
            {`${formatPrice({
                price: ad.price / ad.livingArea,
                lang: i18n.language,
            })}/m²`}
        </Typography>
    );

    return (
        <>
            <Box display="flex" flexDirection={isUnder500 ? 'column' : 'row'} gap="1rem" mt="1rem">
                <RadiusBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    minWidth="11.875rem"
                >
                    <Typography
                        component="span"
                        variant="h3"
                        fontSize="1rem"
                        data-target-id="home-evaluation-seller-price-heading"
                        mb="1.5rem"
                    >
                        {t('ad:homeEvaluation.sellerPrice')}
                    </Typography>

                    <Box display="flex" flexDirection="column" gap="0.5rem" alignItems="flex-start">
                        {isUnder500 ? (
                            <>
                                <Box gap="1rem" display="flex">
                                    {sellerPrice}
                                    {tag}
                                </Box>
                                {sqMeters}
                            </>
                        ) : (
                            <>
                                {sellerPrice}
                                {sqMeters}
                                {tag}
                            </>
                        )}
                    </Box>
                </RadiusBox>
                <Box
                    position="relative"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="flex-start"
                    width="100%"
                    gap="0"
                    flexWrap="wrap"
                >
                    <Box width="100%" paddingX="0.5rem">
                        <ContentItem display="flex" alignItems="flex-start" flexDirection="column">
                            <Box display="flex" alignItems="baseline" gap="0.5rem">
                                <Typography variant="h4" sx={{ fontSize: '1rem' }}>
                                    {t('ad:homeEvaluation.predictedPrice')}*
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center">
                                <Typography variant="subtitle1" sx={{ fontSize: '0.875rem' }}>
                                    {t('ad:homeEvaluation.confidenceRating')} 4/5
                                </Typography>
                                <Tooltip title={t('ad:homeEvaluation.confidenceRatingToolTip')}>
                                    <IconButton size="small" aria-label="delete">
                                        <Icon size="1rem" name="InfoCircle" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </ContentItem>

                        <EvaluationSlider
                            track={false}
                            aria-label={t('ad:homeEvaluation.evaluation')}
                            value={priceEvalPercent[priceEval]}
                            valueLabelDisplay="on"
                            valueLabelFormat={labelContent}
                            readOnly
                        />
                        <Box sx={{ mt: '0.5rem' }} display="flex" justifyContent="space-between" width="100%">
                            {' '}
                            {estimateBounds.map((item) => (
                                <ContentItem
                                    key={item.dataTargetId}
                                    display="flex"
                                    flexDirection="column"
                                    alignItems={item.alignItems}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        fontSize="0.75rem"
                                        lineHeight="0.875rem"
                                        color={colours.primary.main}
                                    >
                                        {t(item.textKey)}
                                    </Typography>
                                    <Typography
                                        variant="subtitle1"
                                        fontSize="0.75rem"
                                        lineHeight="0.875rem"
                                        color={colours.primary.main}
                                        data-target-id={item.dataTargetId}
                                    >
                                        {formatPrice({ price: item.price, lang: i18n.language })}
                                    </Typography>
                                </ContentItem>
                            ))}
                        </Box>
                    </Box>
                    {evaluationStatus !== 'visible' && (
                        <>
                            <BlurBox data-target-id="evaluation-blur" />
                            <Box style={{ position: 'absolute', alignSelf: 'center' }}>
                                {evaluationStatus === 'loading' && <Loader data-target-id="evaluation-loader" />}
                                {evaluationStatus === 'unavailable' && (
                                    <Box
                                        data-target-id="evaluation-unavailable"
                                        display="flex"
                                        flexDirection="column"
                                        gap="0.75rem"
                                    >
                                        <Typography
                                            variant="body1"
                                            fontSize="1.2rem"
                                            fontWeight="bold"
                                            color={colours.primary.main}
                                        >
                                            {t('ad:unavailablePropertyEstimate')}
                                        </Typography>
                                        <Typography variant="body1">
                                            {t('ad:unavailablePropertyEstimateText')}
                                        </Typography>
                                    </Box>
                                )}
                                {evaluationStatus === 'hidden' && (
                                    <Button data-target-id="see-evaluation-button" onClick={onRequestEvaluation}>
                                        {t('ad:seePropertyEvaluation')}
                                    </Button>
                                )}
                            </Box>
                        </>
                    )}
                </Box>
            </Box>
            {evaluationStatus === 'visible' && (
                <Box width="100%" sx={{ mt: '1rem' }}>
                    <Typography variant="body2">*{t('ad:homeEvaluation.disclaimer')}</Typography>
                </Box>
            )}
        </>
    );
};

type RequestEvaluationProps = {
    ad: {
        id: Ad['id'];
        price: number;
        livingArea: number;
    };
};
const RequestEvaluation: React.FC<RequestEvaluationProps> = ({ ad }) => {
    const { requestPropertyEstimation, loading } = useRequestPropertyEstimation();
    const [isEvaluationUnavailable, setIsEvaluationUnavailable] = useState(false);
    const { logEventDynamic } = useDynamicLogEvents();

    const handleRequestEvaluationClick = async () => {
        try {
            logEventDynamic(AnalyticsEvent.RequestEvaluation);
            // Ask for the evaluation, if the mutation is successful, the evaluation will automatically be updated
            const evaluation = await requestPropertyEstimation(ad.id);

            // If the mutation returns null or undefined, it means the evaluation is not available
            if (!evaluation) {
                setIsEvaluationUnavailable(true);
            }
        } catch {
            // If the mutation throws an error, we set the evaluation as unavailable
            setIsEvaluationUnavailable(true);
        }
    };

    const evaluationStatus: EvaluationProps['evaluationStatus'] = useMemo(() => {
        if (loading) {
            return 'loading';
        }
        if (isEvaluationUnavailable) {
            return 'unavailable';
        }
        return 'hidden';
    }, [isEvaluationUnavailable, loading]);

    return (
        <Evaluation
            ad={ad}
            evaluation={{
                confidenceMin: ad.price,
                confidenceMax: ad.price,
                predictedPrice: ad.price,
            }}
            onRequestEvaluation={handleRequestEvaluationClick}
            evaluationStatus={evaluationStatus}
        />
    );
};
